import React, { useState, useRef, useEffect } from "react";
import Stepper from "react-stepper-horizontal";
import { Card } from "react-md";
import { MSpinner } from "components/Shared";
import MCard from "components/Shared/MCard";
import OrderSummary from "components/WebOrderV2/OrderSummary";
import { getSettings, getBillingPreview, submitWebOrder, submitCommercialWebOrder, getStatus } from "requests/webOrder";
import useWebOrderSettings from "hooks/useWebOrderSettings";
import { getEnabledFeatures } from "requests/customer";
import OrderResult from "components/WebOrderV2/OrderResult";
import { PAY_NOW, DEFER_PAYMENT, LENDER, COMMERCIAL } from "typeConstants/webOrder";
import { WebOrderCase, WebOrderResponseBase, WebOrderStatusResponse } from "interfaces/Mueller.Business.Models";
import { getStateAbbreviationList } from "typeConstants/states";
import { stepperThemeProps } from "components/Shared/MStepper";

interface OrderResult {
  success: boolean,
  errorMessages?: string[],
  cases?: WebOrderCase[],
  response?: WebOrderStatusResponse,
};

const WebOrderV2 = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [features, setFeatures] = useState([]);
  const [billingPreview, setBillingPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState<OrderResult>(null);
  const [importQueueAliasId, setImportQueueAliasId] = useState<string>("");
  const [orderStatusTimeout, setOrderStatusTimeout] = useState<number>(0);

  const orderSettings = useWebOrderSettings();

  const mounted = useRef(true);
  const orderData = useRef<any>({});

  const stateOptions = getStateAbbreviationList().map(s => ({ label: s, value: s }));

  useEffect(() => {
    return () => {
      window.clearTimeout(orderStatusTimeout);
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if(importQueueAliasId) {
      getOrderStatus();
    }
  }, [importQueueAliasId]);

  const onNext = data => {
    let totalSteps = orderSettings?.getTotalSteps();
    if (currentStep === totalSteps - 1) {
      runBillingPreview();
      return;
    }

    const billingNeedsRefresh = ("rush" in data && orderData.current?.rush !== data.rush);
    orderData.current = { ...orderData.current, ...data };
    setCurrentStep(currentStep + 1);
    if (billingNeedsRefresh){
      runBillingPreview();
    }
  };

  const onPrevious = data => {
    if (currentStep === 0) return;

    // User may have hit previous on order result
    if (orderResult) {
      setOrderResult(null);
      return;
    }

    orderData.current = { ...orderData.current, ...data };
    setCurrentStep(currentStep - 1);
  };

  const onOrderResult = (response: WebOrderResponseBase) => {
    // End point will return validations issues in a successful response
    if (response.error) {
      setBillingPreview(null);
      setIsSubmitting(false);
      setOrderResult({
        success: false,
        errorMessages: response.errorMessages,
      });
    }
    else {
      setImportQueueAliasId(response.importQueueAliasId); // useEffect hook will start polling status
    }
  };

  const onOrderError = response => {
    let result = {
      success: false,
      errorMessages: [response.message]
    };
    setIsSubmitting(false);
    setOrderResult(result);
  };

  const getOrderStatus = () => {
    getStatus(importQueueAliasId, onOrderStatusResult, onOrderStatusError, mounted);
  };

  const onOrderStatusResult = (response: WebOrderStatusResponse) => {
    if (!response.finished) {
      setOrderStatusTimeout(window.setTimeout(() => {
        getOrderStatus();
      }, 3000));
    }
    else {
      setBillingPreview(null);
      setIsSubmitting(false);
      setImportQueueAliasId("");

      if(response.failed) {
        setOrderResult({
          success: false,
          errorMessages: response.messages,
          response: response,
        });
      }
      else {
        setOrderResult({
          success: true,
          cases: response.cases,
          response: response,
        });
      }
    }
  };

  const onOrderStatusError = () => {
    setBillingPreview(null);
    setIsSubmitting(false);
    setImportQueueAliasId("");
    setOrderResult({
      success: false,
      errorMessages: ["Your order was submitted but there was an error while waiting for the import."],
    });
  };

  const onNewOrder = () => {
    orderData.current = {};
    setOrderResult(null);
    setBillingPreview(null);
    setCurrentStep(0);
  };

  const getOrderSettings = () => {
    let settingsRequest = {
      customerNumber: orderData.current.customer.value,
      surveyTypeId: orderData.current.surveyType.id
    };
    getSettings(settingsRequest, settingsCallback, mounted);
  };

  const settingsCallback = response => {
    // Store this locally to prevent issues with callbacks capturing out of date or null order settings
    orderData.current.availablePaymentTypes = response.availablePaymentTypes;
    orderSettings.updateSettings(response);
    // Need to try to get preview here in case someone filled out address then selected or changed survey type
    runBillingPreview();
  };

  const onAccountChange = option => {
    orderData.current.customer = option;
    orderData.current.surveyType = null;
    getEnabledFeatures(option.value, setFeatures, mounted);
    if (billingPreview) {
      runBillingPreview();
    }
  };

  const onSurveyTypeChange = option => {
    orderData.current.surveyType = option;
    getOrderSettings();
    if (billingPreview) {
      runBillingPreview();
    }
  };

  const onPostalCodeChange = postalCode => {
    orderData.current.surveyZipCode = postalCode;
    if (postalCode?.length !== 5 || !orderData.current.customer?.value || !orderData.current.surveyType?.value) return;
    runBillingPreview();
  };

  const handleBillingPreview = response => {
    if (!response && billingPreview){
      setBillingPreview(null);
      return ;
    }
    let handlingFeatures = orderSettings?.getHandlingFeatures();
    if (hasRetailOrdering() || handlingFeatures.showExpectedPrice) {
      setBillingPreview(`${response}`);
    } else {
      setBillingPreview(null);
    }
  };

  const runBillingPreview = () => {
    let handlingFeatures = orderSettings?.getHandlingFeatures();
    if ((hasRetailOrdering() || handlingFeatures.showExpectedPrice) &&
            orderData.current.surveyType && orderData.current.surveyZipCode && orderData.current.surveyZipCode.length >= 5) {
      const billingInfo = {
        CustomerNumber: orderData.current.customer.value,
        SurveyTypeCode: orderData.current.surveyType.value,
        PostalCode: orderData.current.surveyZipCode
        , IsRush: orderData.current?.rush ?? false
      };
      getBillingPreview(billingInfo, handleBillingPreview, mounted);
      return true;
    }
    setBillingPreview(null);
    return false;
  }

  const hasCreditCardBilling = () => {
    let paymentOptions = orderSettings.getPaymentTypeOptions();
    if (paymentOptions.some(o => o.value === PAY_NOW)) return true;
    return false;
  };

  const hasRetailOrdering = () => {
    return orderData.current.availablePaymentTypes?.some(p => p === PAY_NOW)
    || orderData.current.availablePaymentTypes?.some(p => p === DEFER_PAYMENT);
  };

  // Submit Order

  const onPlaceOrder = data => {
    setIsSubmitting(true);
    orderData.current = { ...orderData.current, ...data };

    let requestData = { // class WebOrderRequestBase
      customerNumber: orderData.current.customer.value,
      surveyTypeCode: orderData.current.surveyType.value,
      orderCodeIn: orderData.current.orderCodeIn ?? null,
      mailingAddress: getMailingAddressInfo(),
      agentInfo: getAgentInfo(),
      comments: orderData.current.additionalInfo, // This goes to special attention notes (webnotes)
      attachments: orderData.current.fileList.map(f => f.id),
      appointment: getAppointmentInfo(),
      startDate: orderData.current.startDate ?? new Date(),
      paymentType: orderData.current.paymentOption?.value,
      billing: getBillingInfo(),
      rush: orderData.current.rush ?? false,
    };

    if(orderSettings?.getPageLayout() === COMMERCIAL) {
      placeCommercialOrder(requestData);
    }
    else {
      placeWebOrder(requestData);
    }
  };

  const placeWebOrder = requestData => {
    // class WebOrderRequest
    requestData.trackingCode = orderData.current.trackingNumber ?? null;
    requestData.surveyAddress = getSurveyAddressInfo();
    requestData.supplements = orderData.current.supplements ?? [];
    requestData.repairNotes = orderData.current.repairNotes;
    requestData.useFreApi = orderData.current.freApi ?? false;
    requestData.useFnmaApi = orderData.current.fnmaApi ?? false;
    requestData.insuranceDetail = getInsuranceDetail();
    requestData.lenderDetail = getLenderDetail();

    submitWebOrder(requestData, onOrderResult, onOrderError, mounted);
  };

  const placeCommercialOrder = requestData => {
    // class CommercialWebOrderRequest
    requestData.businessName = orderData.current.businessName;
    requestData.website = orderData.current.website;
    requestData.businessPhone = orderData.current.businessPhone;
    requestData.inspections = orderData.current.inspections;
    requestData.policyNumber = orderData.current.policyNumber,
    requestData.businessType = orderData.current.businessType?.value ?? null,
    requestData.effectiveDate = orderData.current.effectiveDate ?? null,
    requestData.underwriterCode = orderData.current.underwriterCode ?? null,
    requestData.coverageIn = orderData.current.coverageIn ?? null,

    submitCommercialWebOrder(requestData, onOrderResult, onOrderError, mounted);
  };

  const getMailingAddressInfo = () => {
    if (!orderData.current.mailingAddress1) return getSurveyAddressInfo();

    return {
      address1: orderData.current.mailingAddress1,
      city: orderData.current.mailingCity,
      state: getState(orderData.current.mailingState),
      zipCode: `${orderData.current.mailingZipCode}`
    };
  };

  const getSurveyAddressInfo = () => {
    return {
      address1: orderData.current.surveyAddress1,
      address2: orderData.current.surveyAddress2,      
      city: orderData.current.surveyCity,
      state: getState(orderData.current.surveyState),
      zipCode: `${orderData.current.surveyZipCode}`
    };
  };

  const getAgentInfo = () => {
    return {
      name: orderData.current.agentName,
      code: orderData.current.agentCode,
      phone: orderData.current.agentPhone,
      email: orderData.current.agentEmail,
      mailingAddress: {
        address1: orderData.current.agentStreetAddress1,
        address2: null,
        city: orderData.current.agentCity,
        state: getState(orderData.current.agentState),
        zipCode: `${orderData.current.agentZipCode}`
      }
    };
  };

  const getState = stateValue => {
    // If the state was set by address search, it will be an object
    if (typeof stateValue === "object") {
      return stateValue?.value;
    }
    // If state is entered manually it will be a string
    return stateValue;
  };

  const getContactInfo = () => {
    return {
      firstName: orderData.current.contactFirstName ?? null,
      lastName: orderData.current.contactLastName ?? null,
      phoneNumber1: orderData.current.contactPhoneNumber1 ?? null,
      phoneNumber2: orderData.current.contactPhoneNumber2 ?? null,
      emailAddress: orderData.current.contactEmailAddress ?? null,
      role: null as string,
      address: null as ReturnType<typeof getMailingAddressInfo>,
    };
  };

  const getWebOrderContactInfo = () => {
    let contactInfo = getContactInfo();
    // If hideContactInfoFields, we're using homeowner as contact
    let contactRole = orderData.current.contactRole?.value;
    contactInfo.role = contactRole;
    contactInfo.address = getMailingAddressInfo();
    return contactInfo;
  };

  const getAppointmentInfo = () => {
    let handlingFeatures = orderSettings?.getHandlingFeatures();
    return {
      occupancy: orderData.current.occupancy?.value ?? null,
      contact: handlingFeatures?.isAppointmentRequired ? getWebOrderContactInfo() : null,
    };
  };

  const getBillingInfo = () => {
    if (orderData.current.paymentOption?.value !== PAY_NOW) return null;

    let formatedExpiration = `${orderData.current.expirationYear}-${orderData.current.expirationMonth}`;
    return {
      creditCard: {
        cardNumber: orderData.current.cardNumber,
        expirationDate: formatedExpiration,
        ccv: orderData.current.cardCVC,
        cardHolder: orderData.current.cardHolderName
      }
    };
  };

  const getInsuranceDetail = () => {
    return {
      policyNumber: orderData.current.policyNumber,
      businessType: orderData.current.businessType?.value ?? null,
      effectiveDate: orderData.current.effectiveDate ?? null,
      underwriterCode: orderData.current.underwriterCode ?? null,
      policyholderContactInfo: getPolicyholderContactInfo(),
      coverageIn: orderData.current.coverageIn ?? null,
      yearBuilt: orderData.current.yearBuilt ?? null,
      sqFt: orderData.current.sqFt ?? null
    };
  };

  const getLenderDetail = () => {
    return {
      brokerAgent: orderData.current.brokerId?.value ?? null,
      isCorrespondent: orderData.current.isCorrespondent ?? false, // TODO not sure if needed, not listed in fields?
      loan: getLoanInfo(),
      transactionType: orderData.current.transactionType?.value ?? null,
      borrower: getBorrowerInfo()
    };
  };

  const getLoanInfo = () => {
    return {
      lenderName: orderData.current.customer?.label ?? null,
      loanNumber: orderData.current.policyNumber, // Loan and policy are stored in the same place
      desktopUnderwriterCasefileID: orderData.current.desktopUnderwriterId ?? null,
      propertyType: orderData.current.propertyType?.value ?? null,
      freddieMacCaseFileId: orderData.current.loanProspectorId ?? null, // TODO Only need one of these
      loanProspectorId: orderData.current.loanProspectorId ?? null // TODO Only need one of these
    };
  };

  const getBorrowerInfo = () => {
    return {
      firstName:orderData.current.homeownerFirstName ?? null,
      lastName: orderData.current.homeownerLastName ?? null,
      phoneNumber1: orderData.current.homeownerPhoneNumber1 ?? null,
      phoneNumber2: orderData.current.homeownerPhoneNumber2 ?? null,
      emailAddress: orderData.current.homeownerEmailAddress ?? null,
      noValidEmail: !orderData.current.validEmailAddress,
      borrowerAddress: getMailingAddressInfo(),
    };
  };

  const getPolicyholderContactInfo = () => {
    return {
      firstName: orderData.current.homeownerFirstName ?? null,
      lastName: orderData.current.homeownerLastName ?? null,
      phoneNumber1: orderData.current.homeownerPhoneNumber1 ?? null,
      phoneNumber2: orderData.current.homeownerPhoneNumber2 ?? null,
      emailAddress: orderData.current.homeownerEmailAddress ?? null
    };
  };

  // Render

  const getCurrentStepComponent = () => {
    if (isSubmitting) {
      return (
        <MCard
          title={"Processing Order"}
        >
          <MSpinner />
        </MCard>
      );
    }

    if (orderResult) {
      return (
        <OrderResult
          /* && !orderResult.response.errorCount seemed partially confusing, basically if errorCount is null, undefined, or 0 we are successful (if success also true) */
          orderSuccess={orderResult.success && !orderResult.response.errorCount}
          cases={orderResult.cases}
          errorMessages={orderResult.errorMessages}
          onPrevious={onPrevious}
          onNewOrder={onNewOrder}
          response={orderResult.response}
        />
      );
    }

    const OrderComponent = orderSettings.getComponentForStep(currentStep);
    const terminology = orderSettings?.getTerminology();
    const pageLayout = orderSettings?.getPageLayout();
    const handlingFeatures = orderSettings?.getHandlingFeatures();
    const totalSteps = orderSettings?.getTotalSteps() - 1;
    const availableSupplements = orderSettings?.getSupplements();

    // First step is always Product and requires special props
    if (currentStep === 0) {
      return (
        <OrderComponent
          // common props
          data={orderData.current}
          onPrevious={() => {}}
          onNext={onNext}
          currentStep={currentStep}
          totalSteps={totalSteps}
          terminology={terminology}
          handlingFeatures={handlingFeatures}
          accountFeatures={features}
          pageLayout={pageLayout}

          // step 0 props
          onAccountChange={onAccountChange}
          onSurveyTypeChange={onSurveyTypeChange}
        />
      );
    }
    // Last step requires special props
    if (currentStep === totalSteps) {
      const paymentOptions = orderSettings?.getPaymentTypeOptions();
      return (
        <OrderComponent
          // common props
          data={orderData.current}
          onPrevious={onPrevious}
          onNext={onPlaceOrder}
          currentStep={currentStep}
          totalSteps={totalSteps}
          terminology={terminology}
          handlingFeatures={handlingFeatures}
          accountFeatures={features}
          pageLayout={pageLayout}

          // last step props
          paymentOptions={paymentOptions}
        />
      );
    }
    return (
      <OrderComponent
        // common props
        data={orderData.current}
        onPrevious={onPrevious}
        onNext={onNext}
        currentStep={currentStep}
        totalSteps={totalSteps}
        terminology={terminology}
        handlingFeatures={handlingFeatures}
        accountFeatures={features}
        pageLayout={pageLayout}

        // middle step props
        stateOptions={stateOptions}
        onPostalCodeChange={onPostalCodeChange}
        availableSupplements={availableSupplements}
      />
    );
  };

  return (
    <div className="web-order-container">
      {!orderResult && <Stepper
        steps={orderSettings.getStepList()}
        activeStep={currentStep}
        {...stepperThemeProps}
      />}
      <div className="web-order-content-container">
        <Card className="web-order-form-container">
          {getCurrentStepComponent()}
        </Card>
        {billingPreview &&
          <Card className="order-summary-card">
            <OrderSummary
              minPrice={billingPreview}
              // maxPrice={} // Not currently supported by backend
              creditCardAvailable={hasCreditCardBilling()}
              hasRetailOrdering={hasRetailOrdering()}
            />
          </Card>}
      </div>
    </div>
  );
};

export default WebOrderV2;
